import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './myComps/header';
import Footer from './myComps/Footer';
import Home from './myComps/home';
import Services from './myComps/Services';
import CaseStudies from './myComps/CaseStudies';
import Insights from './myComps/Insights';
import About from './myComps/About';
import Contact from './myComps/Contact';
import Legal from './myComps/Legal';
import ThankYou from './myComps/ThankYou';
import NotFound from './myComps/NotFound';
import Loader from './myComps/Loader';
import ChatWindow from './myComps/chatbot/ChatWindow';

const VALID_PAGES = [
  'home',
  'about',
  'services',
  'case-studies',
  'insights',
  'contact',
  'legal',
  'privacy',
  'terms',
  'thankyou'
];

function App() {
  // Global website loading state (shows on initial visit/reload across all devices)
  const [loading, setLoading] = useState(true);

  // Chatbot modal open/close state (toggled by the orange chat button)
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Theme state: default to 'light'
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme === 'dark' || savedTheme === 'light') return savedTheme;
      } catch (e) {
        // Storage unavailable (private mode / blocked): use default theme
      }
    }
    return 'light';
  });

  // Active page state for single-page component routing
  const [activePage, setActivePage] = useState(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      if (VALID_PAGES.includes(hash)) {
        return hash;
      }
      if (hash && hash !== '') {
        return '404';
      }
    }
    return 'home';
  });

  // Responsive mobile vs desktop detection
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  // Mobile prompt pill state ("Ask me a question! 🤖")
  const [showMobilePrompt, setShowMobilePrompt] = useState(false);

  // Responsive resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-popup chatbot on desktop & display mobile prompt
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mobileView = window.innerWidth <= 768;

      if (!mobileView) {
        // Desktop: Automatically pop up chatbot with greeting animation
        const timer = setTimeout(() => {
          setIsChatOpen(true);
        }, 1200);
        return () => clearTimeout(timer);
      } else {
        // Mobile: Show animated "Ask me a question! 🤖" prompt after 1.2s
        const timer = setTimeout(() => {
          setShowMobilePrompt(true);
        }, 1200);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  // Brief initial website loader (smooth across desktop and mobile devices)
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 700);
    return () => clearTimeout(timer);
  }, []);

  // Keep theme synced with document element & localStorage
  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    document.body.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('theme', theme);
    } catch (e) {
      // Ignore storage errors
    }
  }, [theme]);

  // Listen to hash changes in window (route to 404 if invalid)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (VALID_PAGES.includes(hash)) {
        setActivePage(hash);
      } else if (hash && hash !== '') {
        setActivePage('404');
      } else {
        setActivePage('home');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const handleNavigate = (pageId) => {
    setActivePage(pageId);
    window.location.hash = pageId;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Render current active component with smart fallback to 404
  const renderCurrentPage = () => {
    switch (activePage) {
      case 'about':
        return <About onNavigate={handleNavigate} />;
      case 'services':
        return <Services onNavigate={handleNavigate} />;
      case 'case-studies':
        return <CaseStudies onNavigate={handleNavigate} />;
      case 'insights':
        return <Insights onNavigate={handleNavigate} />;
      case 'contact':
        return <Contact onNavigate={handleNavigate} />;
      case 'thankyou':
        return <ThankYou onNavigate={handleNavigate} />;
      case 'privacy':
        return <Legal initialTab="privacy" onNavigate={handleNavigate} />;
      case 'terms':
        return <Legal initialTab="terms" onNavigate={handleNavigate} />;
      case 'legal':
        return <Legal initialTab="privacy" onNavigate={handleNavigate} />;
      case '404':
        return <NotFound onNavigate={handleNavigate} />;
      case 'home':
        return <Home onNavigate={handleNavigate} />;
      default:
        return <NotFound onNavigate={handleNavigate} />;
    }
  };

  return (
    <div className={`App d-flex flex-column min-vh-100 theme-${theme}`}>
      {loading && <Loader />}

      <Header
        activePage={activePage}
        onNavigate={handleNavigate}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      <main className="flex-grow-1 position-relative">
        {renderCurrentPage()}
      </main>

      {/* MSquare AI Chatbot Window (Opens directly above the orange button) */}
      <ChatWindow
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />

      {/* Floating Actions: Orange Button Launches MSquare AI Chatbot + WhatsApp */}
      <div className="buzz-floating-actions" role="complementary" aria-label="Quick contact actions">
        {/* Launcher Container with Mobile Animated Prompt */}
        <div className="msq-launcher-wrapper">
          {/* Mobile Pop-up speech bubble: "Ask me a question! 🤖" */}
          {isMobile && !isChatOpen && showMobilePrompt && (
            <div
              className="msq-mobile-ask-popup"
              onClick={() => {
                setIsChatOpen(true);
                setShowMobilePrompt(false);
              }}
              role="button"
              tabIndex={0}
              title="Tap to ask MSquare AI Assistant"
            >
              <span className="msq-popup-robot-icon" role="img" aria-label="Robot">🤖</span>
              <span className="msq-popup-text">Ask me a question!</span>
              <button
                type="button"
                className="msq-popup-close-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMobilePrompt(false);
                }}
                aria-label="Dismiss prompt"
              >
                ×
              </button>
              <span className="msq-popup-tail" aria-hidden="true"></span>
            </div>
          )}

          {/* Orange Circular Button - Opens MSquare AI Chatbot */}
          <button
            type="button"
            onClick={() => {
              setIsChatOpen(prev => !prev);
              setShowMobilePrompt(false);
            }}
            className={`buzz-magnetic-cta ${isChatOpen ? 'active' : ''}`}
            aria-label={isChatOpen ? "Close AI Assistant" : "Chat with MSquare AI Assistant"}
            title={isChatOpen ? "Close AI Assistant" : "MSquare AI Assistant — Instant Answers"}
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
          >
            <span className="buzz-cta-spin-ring" aria-hidden="true" />
            <span className="buzz-cta-inner">
              {isChatOpen ? (
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              ) : isMobile ? (
                <span className="msq-robot-emoji-btn" role="img" aria-label="AI Robot">🤖</span>
              ) : (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              )}
            </span>
            <span className="buzz-cta-tooltip">{isChatOpen ? 'Close Chat' : isMobile ? 'Ask me!' : 'AI Assistant'}</span>
          </button>
        </div>

        {/* Floating WhatsApp Quick Action Button */}
        <a
          className="whatsapp-float-btn"
          href="https://api.whatsapp.com/send/?phone=919870202444&text=Hi+MSquare!+I+visited+your+website+and+would+like+to+know+more+about+your+growth+services."
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat with us on WhatsApp"
        >
          <span className="whatsapp-tooltip-label">WhatsApp Us</span>
          <span className="whatsapp-btn-icon" aria-hidden="true">
            <svg viewBox="0 0 32 32" width="28" height="28" fill="currentColor">
              <path d="M16.04 4.4c-6.3 0-11.42 4.98-11.42 11.12 0 2.1.61 4.14 1.77 5.9l-1.88 5.52 5.8-1.82a11.67 11.67 0 0 0 5.73 1.5c6.3 0 11.42-4.98 11.42-11.1 0-6.14-5.12-11.12-11.42-11.12Zm0 20.33c-1.8 0-3.55-.51-5.05-1.48l-.4-.25-3.22 1.01 1.04-3.07-.27-.42a9 9 0 0 1-1.54-5c0-5.09 4.23-9.22 9.44-9.22 5.22 0 9.46 4.13 9.46 9.22 0 5.08-4.24 9.21-9.46 9.21Zm5.18-6.9c-.28-.14-1.67-.8-1.93-.89-.26-.09-.45-.14-.64.14-.19.27-.73.89-.9 1.07-.16.18-.33.2-.61.07-.28-.14-1.18-.42-2.25-1.35-.83-.72-1.39-1.62-1.55-1.89-.16-.28-.02-.43.12-.56.13-.12.28-.32.43-.48.14-.16.19-.27.28-.46.1-.18.05-.34-.02-.48-.07-.14-.64-1.51-.88-2.07-.23-.54-.47-.47-.64-.48h-.55c-.19 0-.5.07-.76.34-.26.28-1 1-1 2.42s1.03 2.8 1.17 2.99c.14.18 2.04 3.04 4.94 4.26.69.29 1.23.46 1.65.59.69.21 1.32.18 1.82.11.56-.08 1.67-.66 1.9-1.3.24-.64.24-1.19.17-1.3-.07-.12-.26-.19-.54-.33Z" />
            </svg>
          </span>
        </a>
      </div>

      <Footer onNavigate={handleNavigate} />
    </div>
  );
}

export default App;
